import React, { useEffect, useState } from "react";
import ProgressBar from "../../components/ProgressBar";
import { useParams, useNavigate } from "react-router-dom";
import { 
  fetchProposalDetails, 
  evaluateProposalResult, 
  castVote,
  checkUserVoteStatus,
  checkVotingPower 
} from "../../config/snapshot";
import { formatTimeDifference } from "../../utils";
import { Spinner, Alert, Modal, Button } from "react-bootstrap";
import { ENV } from "../../config/config";
import { useAccount } from 'wagmi';

const Voting = () => {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  
  const [proposalData, setProposalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resultStatus, setResultStatus] = useState("Pending");
  const [userVoteStatus, setUserVoteStatus] = useState({ hasVoted: false, choice: null });
  const [votingPower, setVotingPower] = useState({ hasVotingPower: false, votingPower: 0 });
  const [voting, setVoting] = useState(false);
  const [showVoteModal, setShowVoteModal] = useState(false);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [voteError, setVoteError] = useState("");
  const [voteSuccess, setVoteSuccess] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const quorum = ENV.quorum;
  const vetoThreshold = ENV.vetoThreshold;
  const voteColors = ["success", "danger", "primary", "warning"]; // Approve, Reject, Abstain, No with veto

  useEffect(() => {
    if (proposalId) {
      getProposalDetail();
      // Auto-refresh every 30 seconds to keep data current
      const interval = setInterval(() => {
        refreshProposalData();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [proposalId]);

  useEffect(() => {
    if (proposalData && address) {
      checkUserStatus();
    }
  }, [proposalData, address]);

  const getProposalDetail = async () => {
    try {
      setLoading(true);
      const res = await fetchProposalDetails(proposalId);
      const proposal = res[0];

      if (!proposal) {
        setVoteError("Proposal not found");
        return;
      }

      const totalVote = proposal?.scores?.reduce((a, b) => a + b, 0) || 0;
      const result = evaluateProposalResult(proposal, quorum, vetoThreshold);

      setProposalData({
        ...proposal,
        totalVote,
      });
      setResultStatus(result);
    } catch (err) {
      console.error("Error fetching proposal:", err);
      setVoteError("Failed to fetch proposal details");
    } finally {
      setLoading(false);
    }
  };

  const refreshProposalData = async () => {
    try {
      setRefreshing(true);
      const res = await fetchProposalDetails(proposalId);
      const proposal = res[0];

      if (proposal) {
        const totalVote = proposal?.scores?.reduce((a, b) => a + b, 0) || 0;
        const result = evaluateProposalResult(proposal, quorum, vetoThreshold);

        setProposalData({
          ...proposal,
          totalVote,
        });
        setResultStatus(result);
        
        // Re-check user status after refresh
        if (address) {
          await checkUserStatus();
        }
      }
    } catch (err) {
      console.error("Error refreshing proposal:", err);
    } finally {
      setRefreshing(false);
    }
  };

  const checkUserStatus = async () => {
    if (!address || !proposalData) return;

    try {
      // Check if user has already voted
      const voteStatus = await checkUserVoteStatus(proposalId, address);
      setUserVoteStatus(voteStatus);

      // Check user's voting power
      const powerStatus = await checkVotingPower(proposalId, address, proposalData.snapshot);
      setVotingPower(powerStatus);
    } catch (error) {
      console.error("Error checking user status:", error);
    }
  };

  const handleVoteClick = (choiceIndex) => {
    setVoteError("");
    setVoteSuccess("");

    if (!isConnected || !address) {
      setVoteError("Please connect your wallet to vote");
      return;
    }

    if (userVoteStatus.hasVoted) {
      setVoteError("You have already voted on this proposal");
      return;
    }

    if (!votingPower.hasVotingPower) {
      setVoteError("You don't have sufficient voting power to participate in this proposal");
      return;
    }

    if (Date.now() / 1000 > proposalData.end) {
      setVoteError("Voting period has ended");
      return;
    }

    if (Date.now() / 1000 < proposalData.start) {
      setVoteError("Voting has not started yet");
      return;
    }

    setSelectedChoice(choiceIndex);
    setShowVoteModal(true);
  };

  const confirmVote = async () => {
    if (selectedChoice === null) return;

    try {
      setVoting(true);
      const result = await castVote(proposalId, selectedChoice + 1, address); // Snapshot uses 1-based indexing

      if (result.success) {
        setVoteSuccess(`Successfully voted for "${proposalData.choices[selectedChoice]}"`);
        setShowVoteModal(false);
        setSelectedChoice(null);
        
        // Immediate refresh after successful vote
        await refreshProposalData();
        
        // Show success message for 5 seconds
        setTimeout(() => {
          setVoteSuccess("");
        }, 5000);
      } else {
        setVoteError(result.error || "Failed to cast vote");
      }
    } catch (error) {
      setVoteError(error.message || "An error occurred while voting");
    } finally {
      setVoting(false);
    }
  };

  const getVoteStatusMessage = () => {
    if (userVoteStatus.hasVoted) {
      const choiceName = proposalData.choices[userVoteStatus.choice - 1]; // Snapshot uses 1-based indexing
      return (
        <Alert variant="success" className="mb-3">
          <div className="d-flex align-items-center">
            <span className="me-2">✓</span>
            <div>
              <strong>You have already voted!</strong><br/>
              <small>Your choice: "{choiceName}" {userVoteStatus.votingPower && `(Voting Power: ${userVoteStatus.votingPower})`}</small>
            </div>
          </div>
        </Alert>
      );
    }

    if (!isConnected || !address) {
      return (
        <Alert variant="warning" className="mb-3">
          <strong>Connect your wallet</strong> to participate in voting
        </Alert>
      );
    }

    if (!votingPower.hasVotingPower) {
      return (
        <Alert variant="danger" className="mb-3">
          <div className="d-flex align-items-center">
            <span className="me-2">⚠️</span>
            <div>
              <strong>Insufficient voting power</strong><br/>
              <small>You need minimum voting power to participate in this proposal</small>
            </div>
          </div>
        </Alert>
      );
    }

    if (Date.now() / 1000 > proposalData?.end) {
      return (
        <Alert variant="secondary" className="mb-3">
          <strong>Voting has ended</strong> - Results are being finalized
        </Alert>
      );
    }

    if (Date.now() / 1000 < proposalData?.start) {
      return (
        <Alert variant="info" className="mb-3">
          <strong>Voting hasn't started yet</strong> - Please wait for the voting period to begin
        </Alert>
      );
    }

    return (
      <Alert variant="success" className="mb-3">
        <div className="d-flex align-items-center">
          <span className="me-2">📊</span>
          <div className="text-start">
            <strong>You can vote!</strong><br/>
            <small>Your voting power: {votingPower.votingPower}</small>
          </div>
        </div>
      </Alert>
    );
  };

  if (loading) {
    return (
      <div className="text-center w-100 py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden"><Spinner /></span>
        </div>
        <p className="mt-2">Fetching proposal data...</p>
      </div>
    );
  }

  if (!proposalData) {
    return (
      <div className="text-center w-100 py-5">
        <Alert variant="danger">
          <h4>Proposal Not Found</h4>
          <p>The requested proposal could not be found.</p>
          <Button variant="primary" onClick={() => navigate('/')}>
            Back to Proposals
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="home-container">
      <div className="container">
        {/* Header with Back Button and Refresh */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Button variant="outline-secondary" onClick={() => navigate('/')}>
            ← Back to Proposals
          </Button>
          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={refreshProposalData}
            disabled={refreshing}
          >
            {refreshing ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-1" />
                Refreshing...
              </>
            ) : (
              "Refresh Data"
            )}
          </Button>
        </div>

        {/* Error/Success Messages */}
        {voteError && (
          <Alert variant="danger" dismissible onClose={() => setVoteError("")}>
            {voteError}
          </Alert>
        )}
        
        {voteSuccess && (
          <Alert variant="success" dismissible onClose={() => setVoteSuccess("")}>
            <strong>Vote Cast Successfully! 🎉</strong><br/>
            {voteSuccess}
          </Alert>
        )}

        {/* Proposal Info */}
        <div className="card-component p-4 mb-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className="d-flex align-items-center gap-3">
              <div className="card-icon">
                <img src="/images/coinmarket.svg" alt="card icon" />
              </div>
              <div>
                <h3 className="mb-1">{proposalData?.title}</h3>
                <span className={`badge ${
                  proposalData?.state === 'active' ? 'bg-success' : 
                  proposalData?.state === 'closed' ? 'bg-secondary' : 'bg-warning'
                }`}>
                  {proposalData?.state?.toUpperCase()}
                </span>
              </div>
            </div>
            {Date.now() / 1000 > proposalData?.end && (
              <span className={`badge fs-6 ${
                resultStatus === 'Approved' ? 'bg-success' : 
                resultStatus.includes('Rejected') ? 'bg-danger' : 'bg-secondary'
              }`}>
                {resultStatus}
              </span>
            )}
          </div>
          
          <div className="proposal-body mb-3">
            <h5>Description</h5>
            <p className="lead">{proposalData?.body}</p>
          </div>
          
          <div className="proposal-meta">
            <small>
              <strong>
                {Date.now() / 1000 > proposalData?.end
                  ? `Ended: ${formatTimeDifference(proposalData?.end)} ago`
                  : `Ends in: ${formatTimeDifference(proposalData?.end)}`}
              </strong>
            </small>
          </div>
        </div>

        {/* Voting Status Message */}
        {getVoteStatusMessage()}

        {/* Voting Section */}
        <div className="card-component p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4>Voting Results</h4>
            {refreshing && (
              <small className="text-primary">
                <Spinner as="span" animation="border" size="sm" className="me-1" />
                Updating...
              </small>
            )}
          </div>

          <div className="vote-grid mb-4">
            <div className="card-component p-2 text-center">
              <h6>Total Votes</h6>
              <h4 className="text-primary">{proposalData.totalVote.toLocaleString()}</h4>
              <small>
                {Date.now() / 1000 > proposalData.end
                  ? resultStatus
                  : proposalData.totalVote > 0
                    ? "Voting In Progress..."
                    : "No votes yet"}
              </small>
            </div>

            {/* Dynamic Vote Options */}
            {proposalData.choices.map((choice, index) => {
              const voteCount = proposalData.scores[index] || 0;
              const percentage = proposalData.totalVote > 0
                ? ((voteCount / proposalData.totalVote) * 100).toFixed(1)
                : "0.0";
              
              const canVote = isConnected && 
                            address && 
                            !userVoteStatus.hasVoted && 
                            votingPower.hasVotingPower && 
                            Date.now() / 1000 <= proposalData.end &&
                            Date.now() / 1000 >= proposalData.start;

              return (
                <div
                  key={index}
                  className={`vote-option-block ${voteColors[index] || "secondary"} ${
                    canVote ? "clickable border-primary" : "border-secondary"
                  } ${userVoteStatus.hasVoted && userVoteStatus.choice === index + 1 ? "border-success border-3" : ""}`}
                  onClick={() => canVote && handleVoteClick(index)}
                  style={{ 
                    cursor: canVote ? "pointer" : "default",
                    opacity: canVote ? 1 : 0.8,
                    position: "relative"
                  }}
                >
                  {userVoteStatus.hasVoted && userVoteStatus.choice === index + 1 && (
                    <div className="position-absolute top-0 end-0 p-1">
                      <span className="badge bg-success">✓ Your Vote</span>
                    </div>
                  )}
                  <h6>{choice}</h6>
                  <div className="percentage text-primary fw-bold">{percentage}%</div>
                  <div className="vote-count">{voteCount.toLocaleString()} votes</div>
                  {canVote && (
                    <small className="text-primary fw-bold">Click to vote</small>
                  )}
                </div>
              );
            })}
          </div>

          <div className="quorum-section">
            <h6>Quorum Progress</h6>
            <div className="row">
              <div className="col-md-6">
                <small><strong>Required:</strong> {quorum.toLocaleString()}</small>
              </div>
              <div className="col-md-6">
                <small><strong>Achieved:</strong> {proposalData.totalVote.toLocaleString()}</small>
              </div>
            </div>
            {/* <ProgressBar progress={(proposalData.totalVote / quorum) * 100} quorum={100} /> */}
            <small>
              {((proposalData.totalVote / quorum) * 100).toFixed(1)}% of required quorum achieved
            </small>
          </div>
        </div>

        {/* Vote Confirmation Modal */}
        <Modal show={showVoteModal} onHide={() => setShowVoteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Your Vote</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="info" className="mb-3">
              <strong>⚠️ Important:</strong> Once you cast your vote, it cannot be changed.
            </Alert>
            <div className="mb-3">
              <p><strong>Proposal:</strong> {proposalData?.title}</p>
              <p><strong className="text-white" >Your Choice:</strong> 
                <span className="badge bg-primary ms-2">
                  {selectedChoice !== null ? proposalData?.choices[selectedChoice] : ""}
                </span>
              </p>
              <p><strong>Your Voting Power:</strong> {votingPower.votingPower}</p>
            </div>
            <p>Are you sure you want to cast this vote?</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowVoteModal(false)} disabled={voting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={confirmVote} disabled={voting}>
              {voting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                  Casting Vote...
                </>
              ) : (
                "✓ Confirm Vote"
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default Voting;